import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { listMeetingSessions } from "../../api/Meetings/meetingSessionsApi";
import { formatMeetingListDate } from "../../utils/meetingListUtils";

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const rise = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const flyPhoto = keyframes`
  0% { opacity: 0.2; transform: translateX(0) scale(0.9); }
  35% { opacity: 1; transform: translateX(28px) scale(1); }
  70% { opacity: 1; transform: translateX(56px) scale(0.95); }
  100% { opacity: 0.25; transform: translateX(72px) scale(0.85); }
`;

const pulseArrow = keyframes`
  0%, 100% { transform: translateX(0); opacity: 0.4; }
  50% { transform: translateX(5px); opacity: 1; }
`;

const bob = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

function MoveExplainStage({ accent }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 72,
        mt: 1.5,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: alpha(accent, 0.1),
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
      }}
    >
      {/* Reunión origen */}
      <Box
        sx={{
          position: "absolute",
          left: 14,
          top: 14,
          width: 44,
          height: 44,
          borderRadius: 1.5,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.35),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 4px 12px ${alpha(accent, 0.12)}`,
        }}
      >
        <EventOutlinedIcon sx={{ fontSize: 22, color: alpha(accent, 0.55) }} />
      </Box>
      {/* Fotos volando */}
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: 52,
            top: 20 + (i % 2) * 6,
            width: 22,
            height: 28,
            borderRadius: 0.75,
            bgcolor: "#fff",
            border: "1.5px solid",
            borderColor: alpha(accent, 0.45),
            boxShadow: `0 4px 10px ${alpha(accent, 0.18)}`,
            animation: `${flyPhoto} 2.8s ease-in-out infinite`,
            animationDelay: `${i * 0.18}s`,
            ...motionSafe,
          }}
        />
      ))}
      <Box
        sx={{
          position: "absolute",
          left: "48%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          color: accent,
          animation: `${pulseArrow} 1.5s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <ArrowForwardRoundedIcon sx={{ fontSize: 26 }} />
      </Box>
      {/* Reunión destino */}
      <Box
        sx={{
          position: "absolute",
          right: 14,
          top: 14,
          width: 44,
          height: 44,
          borderRadius: 1.5,
          bgcolor: "#fff",
          border: "2px solid",
          borderColor: accent,
          display: "grid",
          placeItems: "center",
          boxShadow: `0 6px 16px ${alpha(accent, 0.25)}`,
          animation: `${bob} 2.2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <EventOutlinedIcon sx={{ fontSize: 22, color: accent }} />
      </Box>
    </Box>
  );
}

/**
 * Elige reunión destino para mover fotos/vídeos seleccionados.
 */
export default function MeetingMoveMediaDialog({
  open,
  onClose,
  currentSessionId,
  selectedCount = 0,
  moving = false,
  onConfirm,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessions, setSessions] = useState([]);
  const [query, setQuery] = useState("");
  const [targetId, setTargetId] = useState(null);

  const accent = theme.palette.primary.main || "#0f766e";

  useEffect(() => {
    if (!open) {
      setQuery("");
      setTargetId(null);
      setError("");
      setSessions([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    listMeetingSessions()
      .then((rows) => {
        if (cancelled) return;
        setSessions(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err.response?.data?.error ||
            err.message ||
            t("Meetings.moveMediaLoadError"),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, t]);

  const filtered = useMemo(() => {
    const current = Number(currentSessionId);
    const q = String(query || "")
      .trim()
      .toLowerCase();
    return (sessions || [])
      .filter((s) => Number(s.id) !== current)
      .filter((s) => {
        if (!q) return true;
        const hay = [
          s.title,
          s.customerName,
          s.meetingDate,
          s.ownerUsername,
          String(s.id),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 80);
  }, [sessions, currentSessionId, query]);

  const selectedSession = useMemo(
    () => filtered.find((s) => Number(s.id) === Number(targetId)) || null,
    [filtered, targetId],
  );

  const handleConfirm = () => {
    if (!targetId || moving) return;
    onConfirm?.(Number(targetId));
  };

  return (
    <Dialog
      open={open}
      onClose={() => !moving && onClose?.()}
      fullWidth
      fullScreen={isNarrow}
      maxWidth="sm"
      keepMounted={false}
      transitionDuration={160}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: isNarrow ? 0 : 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          maxHeight: isNarrow ? "100%" : "min(92vh, 820px)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          flexShrink: 0,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: (th) =>
            `linear-gradient(135deg, ${alpha(th.palette.primary.main, 0.12)} 0%, ${alpha(
              th.palette.info.main,
              0.06,
            )} 100%)`,
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(accent, 0.16),
                color: accent,
                flexShrink: 0,
              }}
            >
              <DriveFileMoveOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, lineHeight: 1.15 }}
              >
                {t("Meetings.moveMediaTitle")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.35, mt: 0.25 }}
              >
                {t("Meetings.moveMediaBody", { count: selectedCount })}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label={t("Meetings.cancel")}
            onClick={onClose}
            disabled={moving}
            size="small"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          label={t("Meetings.moveMediaCountChip", { count: selectedCount })}
          sx={{ mt: 1.25, fontWeight: 800, borderRadius: 1.25 }}
        />
        <MoveExplainStage accent={accent} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            mt: 1,
            fontWeight: 600,
            lineHeight: 1.35,
          }}
        >
          {t("Meetings.moveMediaExplain")}
        </Typography>
      </Box>

      <Box
        sx={{
          px: 2.5,
          pt: 1.75,
          pb: 1,
          overflow: "auto",
          flex: 1,
          minHeight: 0,
        }}
      >
        <TextField
          autoFocus={!isNarrow}
          fullWidth
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("Meetings.moveMediaSearch")}
          disabled={moving || loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon
                  fontSize="small"
                  sx={{ color: "text.secondary" }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.5,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: alpha(theme.palette.background.default, 0.55),
            },
          }}
        />

        {error ? (
          <Typography
            color="error"
            variant="body2"
            fontWeight={700}
            sx={{ mb: 1.25 }}
          >
            {error}
          </Typography>
        ) : null}

        {loading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 5 }}>
            <CircularProgress size={32} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box
            sx={{
              py: 4,
              px: 2,
              textAlign: "center",
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "divider",
              bgcolor: alpha(theme.palette.action.hover, 0.25),
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t("Meetings.moveMediaEmpty")}
            </Typography>
          </Box>
        ) : (
          <Stack
            spacing={1}
            sx={{
              maxHeight: { xs: "40dvh", sm: 300 },
              overflowY: "auto",
              pr: 0.25,
            }}
          >
            {filtered.map((s, idx) => {
              const selected = Number(targetId) === Number(s.id);
              const dimmed = Boolean(targetId) && !selected;
              const dateLabel = formatMeetingListDate(
                s.meetingDate || s.startedAt,
                i18n.language,
              );
              return (
                <Box
                  key={s.id}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selected}
                  onClick={() => !moving && setTargetId(Number(s.id))}
                  onKeyDown={(e) => {
                    if (moving) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setTargetId(Number(s.id));
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    p: 1.35,
                    borderRadius: 2,
                    cursor: moving ? "default" : "pointer",
                    border: "2px solid",
                    borderColor: selected
                      ? accent
                      : alpha(theme.palette.text.primary, 0.1),
                    bgcolor: selected
                      ? alpha(accent, 0.12)
                      : alpha(theme.palette.background.paper, 0.9),
                    opacity: dimmed ? 0.5 : 1,
                    filter: dimmed ? "grayscale(0.7)" : "none",
                    transition:
                      "border-color 140ms ease, background-color 140ms ease, opacity 140ms ease, filter 140ms ease",
                    boxShadow: selected
                      ? `0 8px 18px ${alpha(accent, 0.22)}`
                      : "none",
                    animation: `${rise} 280ms ease both`,
                    animationDelay: `${Math.min(idx, 12) * 28}ms`,
                    ...motionSafe,
                    "&:hover": moving
                      ? undefined
                      : {
                          opacity: 1,
                          filter: "none",
                          borderColor: selected ? accent : alpha(accent, 0.45),
                          bgcolor: selected
                            ? alpha(accent, 0.16)
                            : alpha(theme.palette.action.hover, 0.55),
                        },
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      flexShrink: 0,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: selected
                        ? alpha(accent, 0.18)
                        : alpha(theme.palette.text.primary, 0.06),
                      color: selected ? accent : "text.secondary",
                    }}
                  >
                    <EventOutlinedIcon fontSize="small" />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 800,
                        lineHeight: 1.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: selected ? accent : "inherit",
                      }}
                    >
                      {s.title || t("Meetings.untitled")}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        mt: 0.25,
                        lineHeight: 1.35,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {[dateLabel, s.customerName].filter(Boolean).join(" · ")}
                    </Typography>
                  </Box>
                  {selected ? (
                    <CheckCircleRoundedIcon
                      sx={{ flexShrink: 0, fontSize: 22, color: accent }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        border: "2px solid",
                        borderColor: "divider",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        )}

        {selectedSession ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              mt: 1.25,
              lineHeight: 1.4,
              fontWeight: 600,
            }}
          >
            {t("Meetings.moveMediaSelectedHint", {
              title: selectedSession.title || t("Meetings.untitled"),
            })}
          </Typography>
        ) : null}
      </Box>

      <Stack
        direction={{ xs: "column-reverse", sm: "row" }}
        spacing={1.25}
        justifyContent="flex-end"
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
          bgcolor: (th) => alpha(th.palette.background.default, 0.65),
        }}
      >
        <Button
          fullWidth={isNarrow}
          onClick={onClose}
          disabled={moving}
          sx={{ fontWeight: 700, minHeight: 44 }}
        >
          {t("Meetings.cancel")}
        </Button>
        <Button
          fullWidth={isNarrow}
          variant="contained"
          disabled={moving || !targetId}
          onClick={handleConfirm}
          startIcon={
            moving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DriveFileMoveOutlinedIcon />
            )
          }
          sx={{
            minHeight: 48,
            fontWeight: 800,
            borderRadius: 2,
            boxShadow: `0 8px 18px ${alpha(accent, 0.28)}`,
          }}
        >
          {t("Meetings.moveMediaConfirm")}
        </Button>
      </Stack>
    </Dialog>
  );
}
